import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Slot group definitions ───────────────────────────────────────────────────

const ctaDef: SlotGroupDefinition = {
  label: 'Gallery CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore Our Gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function GalleryHomeBlurb() {
  const cta = useSlotGroup('galleryhomeblurb.cta', ctaDef);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full"
      style={{ backgroundColor: 'var(--vibe-color-background, #fff)' }}
    >
      <div className="max-w-3xl px-6 sm:px-10 md:px-16 py-20 md:py-28">
        {/* Subtitle */}
        <RichTextContent
          path="galleryhomeblurb.subtitle"
          label="Subtitle"
          defaultValue="<p>Local Charm</p>"
          as="p"
          className="font-body text-sm uppercase tracking-[0.25em] mb-4"
          style={{ color: 'var(--vibe-color-textMuted, #6B6355)' }}
        />

        {/* Accent divider */}
        <div
          className="w-12 h-[3px] mb-8"
          style={{ backgroundColor: 'var(--vibe-color-primary, #B28C59)' }}
        />

        {/* Title */}
        <RichTextContent
          path="galleryhomeblurb.title"
          label="Title"
          defaultValue="<p>Everything You Need At Your Doorstep</p>"
          as="h2"
          className="font-heading text-4xl sm:text-5xl md:text-[3.5rem] font-bold leading-[1.05] uppercase tracking-wide mb-10"
          style={{ color: 'var(--vibe-color-text, #342E1F)' }}
        />

        {/* Body */}
        <RichTextContent
          path="galleryhomeblurb.body"
          label="Body Text"
          as="div"
          className="font-body text-base leading-relaxed mb-12"
          style={{ color: 'var(--vibe-color-text, #342E1F)', opacity: 0.8 }}
        />

        {/* CTA */}
        <EditableSlotGroup groupPath="galleryhomeblurb.cta" definition={ctaDef}>
          <a
            href={cta.url || '/gallery'}
            target={cta._target || '_self'}
            className="galleryhomeblurb-cta inline-flex items-center px-8 py-3.5 font-body text-sm font-medium tracking-[0.15em] uppercase transition-all duration-300"
          >
            {cta.label || 'Explore Our Gallery'}
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        .galleryhomeblurb-cta {
          background: transparent;
          border: 1.5px solid var(--vibe-color-text, #342E1F);
          border-radius: 2px;
          color: var(--vibe-color-text, #342E1F);
        }
        .galleryhomeblurb-cta:hover {
          background: var(--vibe-color-text, #342E1F);
          color: var(--vibe-color-background, #fff);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .galleryhomeblurb-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryHomeBlurb',
  displayName: 'Gallery Home Blurb',
  tokens: {
    'galleryhomeblurb.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Local Charm</p>',
    },
    'galleryhomeblurb.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Everything You Need At Your Doorstep</p>',
    },
    'galleryhomeblurb.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_gallery_blurb',
      defaultValue:
        '<p>Discover the beauty and charm of your new neighborhood — from local dining and shopping to parks and entertainment, everything you need is right outside your door.</p>',
    },
  },
  slotGroups: {
    'galleryhomeblurb.cta': ctaDef,
  },
};

(GalleryHomeBlurb as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default GalleryHomeBlurb;
